import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';

import type { TimelineMark } from './marks';
import type { Span } from './ranges';
import { clock } from './time';
import { TimelineThumb } from './TimelineThumb';
import { zoomLevels, type ZoomId } from './zoom';

const MIN = 60_000;

interface Props {
  day: [number, number];
  spans: Span[];
  /** Ranges protected as evidence, drawn as a band above the recordings. */
  holds?: Span[];
  /** The camera's events, drawn as ticks under the recordings. */
  marks?: TimelineMark[];
  /** Current position, or null before anything is chosen. */
  playhead: number | null;
  zoom: ZoomId;
  timeZone: string | undefined;
  onSeek: (t: number) => void;
  /** Accessible name of the bar, when a page shows more than one. */
  label?: string;
  /** Hour labels under the bar (off for all but the last of stacked bars). */
  ticks?: boolean;
  /** A thinner bar, for windows where the video keeps the room. */
  compact?: boolean;
  /** The recorded segment at a moment, to preview its frame on hover (server recordings). */
  segmentAt?: (t: number) => string | null;
}

/**
 * The day's recordings as a seekable bar: recorded spans, gaps, the playhead
 * and hour ticks. Zoomed windows follow the playhead. Keyboard: ←/→ one
 * minute, PageUp/PageDown ten minutes, Home/End the day's bounds.
 */
export function Timeline({
  day,
  spans,
  holds = [],
  marks = [],
  playhead,
  zoom,
  timeZone,
  onSeek,
  label = 'Linha do tempo das gravações',
  ticks: showTicks = true,
  compact = false,
  segmentAt,
}: Props) {
  const bar = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const level = zoomLevels.find((z) => z.id === zoom) ?? zoomLevels[0];
  const [dayFrom, dayTo] = day;
  const width = Math.min(level.span, dayTo - dayFrom);
  const center = playhead ?? dayFrom + width / 2;
  const from = Math.min(Math.max(center - width / 2, dayFrom), dayTo - width);
  const to = from + width;
  const pct = (t: number) => ((t - from) / width) * 100;

  const at = (clientX: number) => {
    const r = bar.current?.getBoundingClientRect();
    if (!r || r.width === 0) return from;
    return from + ((clientX - r.left) / r.width) * width;
  };

  // Ticks aligned to wall-clock boundaries of the tenant zone: offsets are
  // whole minutes, so aligning on the day's start is enough.
  const ticks: number[] = [];
  const first = dayFrom + Math.ceil((from - dayFrom) / level.tick) * level.tick;
  for (let t = first; t <= to; t += level.tick) ticks.push(t);

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const base = playhead ?? dayFrom;
    const step: Record<string, number> = {
      ArrowLeft: -MIN,
      ArrowRight: MIN,
      PageDown: -10 * MIN,
      PageUp: 10 * MIN,
    };
    let next: number | null = null;
    if (e.key in step) next = base + (step[e.key] ?? 0);
    else if (e.key === 'Home') next = dayFrom;
    else if (e.key === 'End') next = dayTo - 1000;
    if (next === null) return;
    e.preventDefault();
    onSeek(Math.min(Math.max(next, dayFrom), dayTo - 1000));
  };

  const visible = spans.filter((s) => s.end > from && s.start < to);
  const visibleHolds = holds.filter((s) => s.end > from && s.start < to);
  const visibleMarks = marks.filter((m) => m.last >= from && m.at < to);
  // Marks under the pointer (within about half a percent of the bar).
  const near =
    hover === null
      ? []
      : visibleMarks.filter((m) => m.last >= hover - width / 200 && m.at <= hover + width / 200);
  const box = (s: Span) => ({
    left: `${String(Math.max(pct(s.start), 0))}%`,
    width: `${String(Math.max(Math.min(pct(s.end), 100) - Math.max(pct(s.start), 0), 0.15))}%`,
  });

  return (
    <div className="relative select-none">
      {segmentAt && hover !== null && (
        <TimelineThumb segmentId={segmentAt(hover)} left={pct(hover)} />
      )}
      <div
        ref={bar}
        role="slider"
        tabIndex={0}
        aria-label={label}
        aria-valuemin={dayFrom}
        aria-valuemax={dayTo}
        aria-valuenow={playhead ?? dayFrom}
        aria-valuetext={playhead === null ? 'nenhuma posição' : clock(playhead, timeZone)}
        onKeyDown={onKey}
        onPointerDown={(e: PointerEvent<HTMLDivElement>) => {
          onSeek(Math.min(Math.max(at(e.clientX), dayFrom), dayTo - 1000));
        }}
        onPointerMove={(e) => {
          setHover(at(e.clientX));
        }}
        onPointerLeave={() => {
          setHover(null);
        }}
        className={`relative ${compact ? 'h-6' : 'h-10'} cursor-pointer overflow-hidden rounded-md border border-line bg-canvas focus-visible:outline-2 focus-visible:outline-accent`}
      >
        {/* Recorded spans; the empty track between them is a gap. */}
        {visible.map((s) => (
          <div
            key={s.start}
            className={`absolute ${compact ? 'inset-y-1' : 'inset-y-2'} rounded-sm bg-accent/70`}
            style={box(s)}
          />
        ))}
        {/* Evidence holds: a band on top, listed in text below the timeline. */}
        {visibleHolds.map((s) => (
          <div
            key={`h${String(s.start)}-${String(s.end)}`}
            data-testid="hold-band"
            className="absolute top-0.5 h-1 rounded-sm bg-warn"
            style={box(s)}
          />
        ))}
        {/* Events: a tick per bucket, coloured by the highest severity. */}
        {visibleMarks.map((m) => (
          <div
            key={`m${String(m.at)}-${m.label}`}
            data-testid="event-mark"
            className={`pointer-events-none absolute bottom-0.5 h-1.5 rounded-sm ${
              m.tone === 'crit' ? 'bg-crit' : m.tone === 'warn' ? 'bg-warn' : 'bg-fg-muted'
            }`}
            style={{
              left: `${String(Math.max(pct(m.at), 0))}%`,
              width: `max(2px, ${String(Math.max(Math.min(pct(m.last), 100) - Math.max(pct(m.at), 0), 0))}%)`,
            }}
          />
        ))}
        {hover !== null && (
          <div
            className="pointer-events-none absolute inset-y-0 w-px bg-fg-subtle"
            style={{ left: `${String(pct(hover))}%` }}
          />
        )}
        {playhead !== null && playhead >= from && playhead <= to && (
          <div
            className="pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-fg"
            style={{ left: `${String(pct(playhead))}%` }}
          />
        )}
        {hover !== null && (
          <div
            className="pointer-events-none absolute top-0.5 z-10 -translate-x-1/2 rounded bg-surface-raised px-1.5 py-0.5 font-mono text-2xs whitespace-nowrap text-fg shadow"
            // A label makes the tip wider: keep it inside the bar.
            style={{
              left: `${String(Math.min(Math.max(pct(hover), near[0] ? 14 : 4), near[0] ? 86 : 96))}%`,
            }}
          >
            {clock(hover, timeZone)}
            {near[0] &&
              ` · ${near[0].label}${near.length > 1 ? ` (+${String(near.length - 1)})` : ''}`}
          </div>
        )}
      </div>
      {showTicks && (
        <div aria-hidden="true" className="relative mt-1 h-4 text-2xs text-fg-subtle">
          {ticks.map((t) => {
            const labelled = (t - dayFrom) % level.labelEvery === 0;
            // The first and last labels stay inside the bar (cut off on phones otherwise).
            const at = pct(t);
            const edge = at < 1 ? 'start' : at > 99 ? 'end' : 'mid';
            return (
              <span
                key={t}
                className={`absolute top-0 ${
                  edge === 'start' ? '' : edge === 'end' ? '-translate-x-full' : '-translate-x-1/2'
                }`}
                style={{ left: `${String(at)}%` }}
              >
                <span
                  className={`block h-1 w-px ${
                    edge === 'start' ? 'mr-auto' : edge === 'end' ? 'ml-auto' : 'mx-auto'
                  } ${labelled ? 'bg-fg-subtle' : 'bg-line-strong'}`}
                />
                {labelled && <span className="font-mono">{clock(t, timeZone).slice(0, 5)}</span>}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
